import axios, { AxiosError } from 'axios';
import { API_BASE_URL, TENANT_ID } from '@libs/config';
import { clearUserLocalData } from '@libs/localStorage';
import { log } from '@libs/logger';

const logger = log.tags('API');

const createRequest = (tenantId?: string | null) => {
  if (tenantId) {
    localStorage.setItem('fx_tenantId', tenantId);
  }
  const request = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
      ...(TENANT_ID ? { 'X-Tenant-Id': TENANT_ID } : {}),
    },
  });
  request.interceptors.request.use(
    (config) => {
      const paramsSerialized = Object.keys(config.params || {})
        .reduce<string[]>((acc, paramName) => {
          if (Array.isArray(config.params?.[paramName])) {
            acc.push(...config.params?.[paramName].map((value) => `${paramName}=${value}`));
            return acc;
          }
          acc.push(`${paramName}=${config.params?.[paramName]}`);
          return acc;
        }, [])
        .join('&');

      const url = `${config.baseURL || ''}${config.url || ''}?${paramsSerialized}`;
      const headersFiltered = Object.keys(config.headers)
        .filter((key: string) => {
          return !['Authorization'].includes(key);
        })
        .reduce<Record<string, string>>((acc, key) => {
          return { ...acc, [key]: config.headers[key] };
        }, {});
      const headers = Object.keys(headersFiltered).length ? `Headers: ${JSON.stringify(headersFiltered, null, 2)}` : '';
      const data =
        config.data && config.method?.toUpperCase() === 'POST' ? `Data: ${JSON.stringify(config.data, null, 2)}` : '';
      const method = config.method?.toUpperCase() || 'GET';
      const logMessage = `>>>>> REQUEST:      [${method}]: ${url}${headers && `\n${headers}`}${data && `\n${data}`}`;
      logger.apiMethod(logMessage);
      return config;
    },
    (error) => {
      logger.apiMethod(`>>>>> REQUEST ERROR: ${error}`);
      return Promise.reject(error);
    },
  );

  request.interceptors.response.use(
    (response) => {
      const headersFiltered = Object.keys(response.headers)
        .filter((key: string) => {
          return !['Authorization'].includes(key);
        })
        .reduce<Record<string, string>>((acc, key) => {
          return { ...acc, [key]: response.headers[key] };
        }, {});
      const headers = Object.keys(headersFiltered).length ? `Headers: ${JSON.stringify(headersFiltered, null, 2)}` : '';
      const data = response.data ? `Data: ${JSON.stringify(response.data, null, 2)}` : '';
      const status = response.status;
      const method = response.config.method?.toUpperCase() || 'GET';
      const url = response.request.responseURL || response.config?.url || 'Unknown URL';
      const logMessage = `<<<<<< RESPONSE: ${status}[${method}]: ${url} ${headers && `\n${headers}`}${data && `\n${data}`}`;
      logger.apiMethod(logMessage);
      return response;
    },
    (error) => {
      const method = error.config?.method?.toUpperCase() || 'GET';
      const url = error.request?.responseURL || error.config?.url || 'Unknown URL';
      const status = error.response?.status || '000';
      const data = error.response?.data
        ? `Data: ${JSON.stringify(error.response.data, null, 2)}`
        : 'No data in response';
      const wrapper = `\n======================================================================\n`;
      const logMessage = `${wrapper}!!!!!! SERVER ERROR: ${status}[${method}]${url}\n${data}${wrapper}`;
      logger.apiMethod(logMessage);
      return Promise.reject(error);
    },
  );
  return request;
};

const tenantId = localStorage.getItem('fx_tenantId');
export let request = createRequest(tenantId);

export const applyTenantId = (tenantId?: string) => {
  if (tenantId) {
    request = createRequest(tenantId);
  }
  return request;
};

export const updateToken = (token: string | null) => {
  if (!token) {
    request.defaults.headers['Authorization'] = null;
    return;
  }
  request.defaults.headers['Authorization'] = `Bearer ${token}`;
};

const initialToken = localStorage.getItem('userdata:token');
updateToken(initialToken);

/**
 * Catch the unauthorized Request
 */
request.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      console.log('Unauthorized access - redirecting to login');
      clearUserLocalData();
      // @ts-expect-error `string` is assignable to location
      window.location = '/login';
    }

    return Promise.reject(error);
  },
);
